import React from "react";

import { assertNever } from "@impero-com/common/utils/obj";
import { Typography, InputNumber } from "antd";

import { MultiInputHelper } from "utils/multiInputHelper";

import { BaseFormDisplay } from "./formDisplay";

import { BaseFormField } from ".";

const helper: MultiInputHelper<number, string> = new MultiInputHelper({
    isEqual: (a: number, b: number) => a === b,
    isBlank: (_: number) => false,
    display: (val: number) => val.toString(),
});

type MultiNumericInputProps =
    | ({ type: "CREATE" } & WrappedWriteProps)
    | ({ type: "EDIT" } & WrappedWriteProps)
    | ({ type: "READ_ONLY" } & WrappedReadProps);

type RawMultiNumericInputProps =
    | ({ type: "CREATE" } & WriteProps)
    | ({ type: "EDIT" } & WriteProps)
    | ({ type: "READ_ONLY" } & ReadProps);

type Wrapped<T> = T & { label: string };

type ReadOnlyDisplay = "Text" | "Input";

interface CommonProps {
    name: string;
    values?: number[];
    addonAfter?: React.ReactNode;
    className?: string;
}

interface ReadProps extends CommonProps {
    display: ReadOnlyDisplay;
}
type WrappedReadProps = Wrapped<ReadProps>;

interface WriteProps extends CommonProps {
    onChange: (v: number | undefined) => void;
    min?: number;
    max?: number;
    step?: number | string;
}

type WrappedWriteProps = Wrapped<WriteProps>;

class MultiNumericInput extends React.PureComponent<MultiNumericInputProps> {
    render() {
        const { props } = this;
        switch (props.type) {
            case "READ_ONLY": {
                const { label, name, values, display } = props;
                return (
                    <WrappedReadOnlyNumericInput
                        label={label}
                        name={name}
                        values={values}
                        display={display}
                    />
                );
            }
            case "EDIT": {
                const { label, name, values, onChange, min, max, step } = props;
                return (
                    <WrappedEditNumericInput
                        label={label}
                        name={name}
                        values={values}
                        onChange={onChange}
                        min={min}
                        max={max}
                        step={step}
                    />
                );
            }
            case "CREATE": {
                const { label, name, values, onChange, min, max, step } = props;
                return (
                    <WrappedCreateNumericInput
                        label={label}
                        name={name}
                        values={values}
                        onChange={onChange}
                        min={min}
                        max={max}
                        step={step}
                    />
                );
            }
            default:
                return assertNever(props);
        }
    }
}

type RawReadOnlyNumericInputProps = ReadProps;

class RawReadOnlyNumericInput extends React.PureComponent<RawReadOnlyNumericInputProps> {
    render() {
        const { display } = this.props;
        switch (display) {
            case "Text":
                return this.renderText();
            case "Input":
                return this.renderInput();
            default:
                return assertNever(display);
        }
    }

    private renderText(): React.ReactNode {
        const { value, type } = helper.deriveReadOnlyValueAndType(
            this.props.values
        );
        return <Typography.Text type={type}>{value}</Typography.Text>;
    }

    private renderInput(): React.ReactNode {
        const { value, placeholder } = helper.deriveValueAndPlaceholder(
            this.props.values
        );
        return (
            <InputNumber
                disabled={true}
                value={value}
                placeholder={placeholder}
                addonAfter={this.props.addonAfter}
                className={this.props.className}
            />
        );
    }
}

interface RawEditableNumericInputProps extends WriteProps {
    autoFocus?: boolean;
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    disabled?: boolean;
}

interface RawEditableNumericInputState {
    derivedValue: number | undefined;
    trackedValue: number | undefined;
    placeholder: string;
}

class RawEditableNumericInput extends React.Component<
    RawEditableNumericInputProps,
    RawEditableNumericInputState
> {
    private innerField = React.createRef<HTMLInputElement>();

    state: RawEditableNumericInputState = {
        derivedValue: undefined,
        trackedValue: undefined,
        placeholder: "",
    };

    constructor(props: RawEditableNumericInputProps) {
        super(props);

        const placeholder = helper.derivePlaceholder(props.values);
        this.state = {
            derivedValue: undefined,
            trackedValue: undefined,
            placeholder,
        };
    }

    static getDerivedStateFromProps(
        props: RawEditableNumericInputProps,
        prevState: RawEditableNumericInputState
    ) {
        const { value, placeholder } = helper.deriveValueAndPlaceholder(
            props.values
        );
        if (value !== prevState.derivedValue) {
            return {
                derivedValue: value,
                trackedValue: value,
                placeholder,
            };
        }
        return null;
    }

    render() {
        const { trackedValue, placeholder } = this.state;
        const { autoFocus, onFocus, min, max, step, addonAfter, className } =
            this.props;

        return (
            <InputNumber
                ref={this.innerField}
                autoFocus={autoFocus}
                value={trackedValue}
                onChange={this.onChange}
                onFocus={onFocus}
                onBlur={this.onBlur}
                onPressEnter={this.blur}
                min={min}
                max={max}
                step={step}
                placeholder={placeholder}
                addonAfter={addonAfter}
                className={className}
            />
        );
    }

    onChange = (v: number | null) => {
        if (typeof v !== "number") {
            return;
        }

        const { min, max } = this.props;
        if (min !== undefined) {
            v = Math.max(v, min);
        }
        if (max !== undefined) {
            v = Math.min(v, max);
        }

        this.setState({ trackedValue: v });
        if (this.didChange()) {
            this.props.onChange(v);
        }
    };

    onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        this.props.onBlur?.(event);
        if (this.didChange()) {
            this.props.onChange(this.state.trackedValue);
        }
    };

    blur = () => {
        if (this.innerField.current) {
            this.innerField.current.blur();
        }
    };

    private didChange() {
        return this.state.derivedValue !== this.state.trackedValue;
    }
}

class WrappedReadOnlyNumericInput extends React.PureComponent<WrappedReadProps> {
    render() {
        const { label, name, values, display } = this.props;
        return (
            <BaseFormDisplay label={label}>
                <RawReadOnlyNumericInput
                    name={name}
                    values={values}
                    display={display}
                />
            </BaseFormDisplay>
        );
    }
}

interface WrappedEditNumericInputState {
    isEditing: boolean;
}

class WrappedEditNumericInput extends React.Component<
    WrappedWriteProps,
    WrappedEditNumericInputState
> {
    state = {
        isEditing: false,
    };

    render() {
        const { label, name, values, onChange, min, max, step } = this.props;
        const { isEditing } = this.state;

        return (
            <BaseFormField
                getComponent={() => (
                    <RawEditableNumericInput
                        name={name}
                        autoFocus={true}
                        values={values}
                        onBlur={this.onBlur}
                        onChange={onChange}
                        min={min}
                        max={max}
                        step={step}
                    />
                )}
                getReadonlyComponent={() => (
                    <RawReadOnlyNumericInput
                        name={name}
                        values={values}
                        display="Text"
                    />
                )}
                onClick={this.onClick}
                hasFeedback={false}
                label={label}
                readonly={!isEditing}
            />
        );
    }

    onBlur = (_: React.FocusEvent<HTMLInputElement>) => {
        this.setState({
            isEditing: false,
        });
    };

    onClick = () => {
        this.setState({
            isEditing: true,
        });
    };
}

class WrappedCreateNumericInput extends React.PureComponent<WrappedWriteProps> {
    render() {
        const { label, name, values, onChange, min, max, step } = this.props;
        return (
            <BaseFormField
                getComponent={() => (
                    <RawEditableNumericInput
                        name={name}
                        values={values}
                        onChange={onChange}
                        min={min}
                        max={max}
                        step={step}
                    />
                )}
                hasFeedback={false}
                label={label}
            />
        );
    }
}

class RawMultiNumericInput extends React.PureComponent<RawMultiNumericInputProps> {
    render() {
        switch (this.props.type) {
            case "CREATE":
            case "EDIT": {
                const {
                    name,
                    values,
                    onChange,
                    min,
                    max,
                    step,
                    addonAfter,
                    className,
                } = this.props;
                return (
                    <RawEditableNumericInput
                        name={name}
                        values={values}
                        onChange={onChange}
                        min={min}
                        max={max}
                        step={step}
                        addonAfter={addonAfter}
                        className={className}
                    />
                );
            }
            case "READ_ONLY": {
                const { name, values, display, addonAfter, className } =
                    this.props;
                return (
                    <RawReadOnlyNumericInput
                        name={name}
                        values={values}
                        display={display}
                        addonAfter={addonAfter}
                        className={className}
                    />
                );
            }
            default:
                return assertNever(this.props);
        }
    }
}

export {
    MultiNumericInput,
    type MultiNumericInputProps,
    RawMultiNumericInput,
    type RawMultiNumericInputProps,
};
